import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin, { Draggable } from '@fullcalendar/interaction';
import type { EventReceiveArg } from '@fullcalendar/interaction';
import type { EventApi, EventChangeArg } from '@fullcalendar/core';
import { X } from 'lucide-react';
import {
  TimetableApi,
  TimetableDay,
  TimetableOverlapError,
  TimetablePalette,
  TimetableSectionGrid,
  TimetableSlot,
} from '@/services/school.api';
import { toast } from '@/stores/toast.store';

const DAY_LABEL: Record<TimetableDay, string> = {
  Monday: 'Mon',
  Tuesday: 'Tue',
  Wednesday: 'Wed',
  Thursday: 'Thu',
  Friday: 'Fri',
  Saturday: 'Sat',
  Sunday: 'Sun',
};

// A fixed, arbitrary reference week — FullCalendar needs real dates to lay
// out a timeGrid view, but this data is weekly/day-of-week keyed, not
// date-keyed. 2024-01-01 was a real Monday; only the weekday matters, never
// the actual calendar date. `firstDay: 1` keeps Sunday as the last column,
// matching the read-only grid's day order.
const REF_MONDAY = new Date(2024, 0, 1); // local midnight, Mon 1 Jan 2024
const DATE_FOR_DAY: Record<TimetableDay, string> = (() => {
  const order: TimetableDay[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const out = {} as Record<TimetableDay, string>;
  order.forEach((day, i) => {
    const d = new Date(REF_MONDAY);
    d.setDate(d.getDate() + i);
    out[day] = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  return out;
})();
// One day past Sunday — `validRange.end` is EXCLUSIVE, so this must be the
// day AFTER the last day we want visible, or Sunday itself (where all the
// real data lives, being Sunday-heavy) gets clipped out of the valid range.
const REF_WEEK_END_EXCLUSIVE = (() => {
  const d = new Date(REF_MONDAY);
  d.setDate(d.getDate() + 7);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
})();
const DAY_FOR_JS_INDEX: TimetableDay[] = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

function pad2(n: number) {
  return String(n).padStart(2, '0');
}
function hhmm(d: Date) {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function slotTitle(subject: string | null, teacher: string): string {
  if (!subject) return 'Break';
  return teacher ? `${subject} — ${teacher}` : subject;
}

function eventFromSlot(day: TimetableDay, slot: TimetableSlot) {
  const isBreak = slot.type === 'Break';
  return {
    id: slot.id,
    title: slotTitle(slot.subject, slot.teacher),
    start: `${DATE_FOR_DAY[day]}T${slot.startTime}:00`,
    end: `${DATE_FOR_DAY[day]}T${slot.endTime}:00`,
    backgroundColor: isBreak ? '#94a3b8' : slot.teacher === 'Unassigned' ? '#f59e0b' : '#2563eb',
    borderColor: isBreak ? '#94a3b8' : slot.teacher === 'Unassigned' ? '#f59e0b' : '#2563eb',
  };
}

interface PaletteTileData {
  title: string;
  duration: string;
  backgroundColor: string;
  extendedProps: { subjectTeacherId?: string; subjectId?: string };
}

export function TimetableCalendarEditor({ sectionId, onDone }: { sectionId: string; onDone: () => void }) {
  const qc = useQueryClient();
  const calendarRef = useRef<FullCalendar>(null);
  const paletteRef = useRef<HTMLDivElement>(null);
  const [pending, setPending] = useState(false);

  const { data: grid } = useQuery<TimetableSectionGrid>({
    queryKey: ['timetable', 'section', sectionId],
    queryFn: () => TimetableApi.sectionGrid(sectionId),
  });
  const { data: palette } = useQuery<TimetablePalette>({
    queryKey: ['timetable', 'palette', sectionId],
    queryFn: () => TimetableApi.palette(sectionId),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['timetable', 'section', sectionId] });

  const createSlot = useMutation({
    mutationFn: TimetableApi.createSlot,
    onError: (e) => toast.error(errMsg(e)),
    onSettled: invalidate,
  });
  const updateSlot = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Parameters<typeof TimetableApi.updateSlot>[1] }) =>
      TimetableApi.updateSlot(id, payload),
    onError: (e) => toast.error(errMsg(e)),
  });
  const removeSlot = useMutation({
    mutationFn: TimetableApi.removeSlot,
    onError: (e) => toast.error(errMsg(e)),
  });
  const removeAll = useMutation({
    mutationFn: () => TimetableApi.removeAllForSection(sectionId),
    onSuccess: () => {
      toast.success('Timetable cleared for this section.');
      invalidate();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const initialEvents = useMemo(() => {
    if (!grid) return [];
    const out: ReturnType<typeof eventFromSlot>[] = [];
    for (const day of grid.days) {
      for (const slot of grid.slots[day] ?? []) out.push(eventFromSlot(day, slot));
    }
    return out;
  }, [grid]);

  const [slotMinTime, slotMaxTime] = useMemo(() => {
    if (!grid) return ['07:00:00', '18:00:00'];
    let min = '09:00';
    let max = '17:00';
    let any = false;
    for (const day of grid.days) {
      for (const slot of grid.slots[day] ?? []) {
        any = true;
        if (slot.startTime < min) min = slot.startTime;
        if (slot.endTime > max) max = slot.endTime;
      }
    }
    if (!any) return ['07:00:00', '18:00:00'];
    return [`${min}:00`, `${max}:00`];
  }, [grid]);

  // Wire up the external draggable palette once its DOM exists.
  useEffect(() => {
    if (!paletteRef.current) return;
    const draggable = new Draggable(paletteRef.current, {
      itemSelector: '.timetable-palette-tile',
      eventData: (el) => {
        const raw = el.getAttribute('data-event');
        return raw ? (JSON.parse(raw) as PaletteTileData) : {};
      },
    });
    return () => draggable.destroy();
  }, [palette]);

  const handleReceive = (info: EventReceiveArg) => {
    const day = DAY_FOR_JS_INDEX[info.event.start!.getDay()];
    const startTime = hhmm(info.event.start!);
    const endTime = hhmm(info.event.end ?? info.event.start!);
    const ext = info.event.extendedProps as PaletteTileData['extendedProps'];
    info.event.remove();
    setPending(true);

    const payload = { sectionId, day, startTime, endTime, subjectTeacherId: ext.subjectTeacherId, subjectId: ext.subjectId };
    const addEvent = (slot: TimetableSlot) => calendarRef.current?.getApi().addEvent(eventFromSlot(day, slot));

    createSlot.mutate(payload, {
      onSuccess: (slot) => {
        addEvent(slot);
        setPending(false);
      },
      onError: (e) => {
        const conflicts = overlapConflicts(e);
        if (conflicts && confirmOverlap(conflicts)) {
          createSlot.mutate(
            { ...payload, force: true },
            { onSuccess: addEvent, onSettled: () => setPending(false) },
          );
          return;
        }
        setPending(false);
      },
    });
  };

  const handleChange = (info: EventChangeArg) => {
    const day = DAY_FOR_JS_INDEX[info.event.start!.getDay()];
    const startTime = hhmm(info.event.start!);
    const endTime = hhmm(info.event.end ?? info.event.start!);
    const payload = { day, startTime, endTime };

    updateSlot.mutate(
      { id: info.event.id, payload },
      {
        onError: (e) => {
          const conflicts = overlapConflicts(e);
          if (conflicts && confirmOverlap(conflicts)) {
            updateSlot.mutate({ id: info.event.id, payload: { ...payload, force: true } }, { onError: () => info.revert() });
            return;
          }
          info.revert();
        },
      },
    );
  };

  const handleRemove = (event: EventApi) => {
    removeSlot.mutate(event.id, { onSuccess: () => event.remove() });
  };

  return (
    <div className="flex flex-col gap-4 lg:flex-row">
      <div className="lg:w-64 lg:shrink-0">
        <div className="card p-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
            Drag onto the calendar
          </div>
          <div ref={paletteRef} className="space-y-1.5">
            {(palette?.subjects ?? []).map((subj) =>
              subj.teachers.length > 0 ? (
                subj.teachers.map((t) => (
                  <PaletteTile
                    key={t.subjectTeacherId}
                    label={`${subj.subjectName} — ${t.teacherName ?? 'Unassigned'}`}
                    color="#2563eb"
                    event={{
                      title: `${subj.subjectName} — ${t.teacherName ?? 'Unassigned'}`,
                      duration: '00:45:00',
                      backgroundColor: '#2563eb',
                      extendedProps: { subjectTeacherId: t.subjectTeacherId },
                    }}
                  />
                ))
              ) : (
                <PaletteTile
                  key={subj.subjectId}
                  label={`${subj.subjectName} (no teacher)`}
                  color="#2563eb"
                  event={{
                    title: subj.subjectName,
                    duration: '00:45:00',
                    backgroundColor: '#2563eb',
                    extendedProps: { subjectId: subj.subjectId },
                  }}
                />
              ),
            )}
            <PaletteTile
              label="Break"
              color="#94a3b8"
              event={{ title: 'Break', duration: '00:30:00', backgroundColor: '#94a3b8', extendedProps: {} }}
            />
          </div>
          {!palette?.subjects.length && (
            <p className="mt-2 text-xs text-slate-400">
              No subjects assigned to this section yet — set them up under Class Section & Teachers.
            </p>
          )}
        </div>

        <button
          className="btn-secondary mt-3 w-full text-sm"
          onClick={() => {
            if (window.confirm("Delete this section's ENTIRE timetable? This cannot be undone.")) {
              removeAll.mutate();
              calendarRef.current?.getApi().getEvents().forEach((e) => e.remove());
            }
          }}
          disabled={removeAll.isPending}
        >
          Delete section's timetable
        </button>
        <button className="btn-primary mt-2 w-full text-sm" onClick={onDone}>
          Done editing
        </button>
      </div>

      <div className="flex-1 rounded-lg border border-slate-200 bg-white p-2 shadow-sm">
        <FullCalendar
          ref={calendarRef}
          plugins={[timeGridPlugin, interactionPlugin]}
          initialView="timeGridWeek"
          initialDate={DATE_FOR_DAY.Monday}
          validRange={{ start: DATE_FOR_DAY.Monday, end: REF_WEEK_END_EXCLUSIVE }}
          firstDay={1}
          headerToolbar={false}
          allDaySlot={false}
          height="auto"
          slotMinTime={slotMinTime}
          slotMaxTime={slotMaxTime}
          slotDuration="00:15:00"
          editable
          eventResizableFromStart
          eventDurationEditable
          droppable
          dayHeaderContent={(arg) => DAY_LABEL[DAY_FOR_JS_INDEX[arg.date.getDay()]]}
          events={initialEvents}
          eventReceive={handleReceive}
          eventChange={handleChange}
          eventContent={(arg) => (
            <div className="flex h-full items-start justify-between gap-1 overflow-hidden px-1 py-0.5 text-[11px] leading-tight text-white">
              <span className="truncate">{arg.event.title}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(arg.event);
                }}
                className="shrink-0 opacity-70 hover:opacity-100"
                title="Remove"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          )}
        />
        {pending && <div className="mt-2 text-xs text-slate-400">Saving…</div>}
      </div>
    </div>
  );
}

function PaletteTile({ label, color, event }: { label: string; color: string; event: PaletteTileData }) {
  return (
    <div
      className="timetable-palette-tile cursor-grab rounded-md px-2 py-1.5 text-xs font-medium text-white active:cursor-grabbing"
      style={{ backgroundColor: color }}
      data-event={JSON.stringify(event)}
    >
      {label}
    </div>
  );
}

function errMsg(e: unknown): string {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

/** Warn-but-allow overlap check (timetable.service.ts's assertNoOverlap) surfaces as a 409 with this shape — everything else is a real error. */
function overlapConflicts(e: unknown): TimetableOverlapError['details'] | null {
  const anyE = e as any;
  if (anyE?.response?.status !== 409) return null;
  const err = anyE.response.data?.error as TimetableOverlapError | undefined;
  return err?.code === 'OVERLAP_WARNING' ? err.details : null;
}

function confirmOverlap(conflicts: TimetableOverlapError['details']): boolean {
  const lines = conflicts.map((c) => `  ${c.startTime}–${c.endTime} — ${c.subject ?? 'Break'}${c.teacher ? ` (${c.teacher})` : ''}`);
  return window.confirm(
    `This overlaps an existing slot for this section:\n\n${lines.join('\n')}\n\nSave anyway?`,
  );
}
